'use client';

import { useEffect, useRef, useState } from 'react';

import { Pause, Play, Volume2, VolumeX } from 'lucide-react';

import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

const VIDEO_LABELS = {
  en: {
    playWithSound: 'Play video with sound',
    playWithSoundShort: 'Play with sound',
    pause: 'Pause video',
    play: 'Play video',
    unmute: 'Unmute video',
    mute: 'Mute video',
  },
  ar: {
    playWithSound: 'شغّل الفيديو مع الصوت',
    playWithSoundShort: 'شغّل مع الصوت',
    pause: 'أوقف الفيديو',
    play: 'شغّل الفيديو',
    unmute: 'شغّل الصوت',
    mute: 'اكتم الصوت',
  },
} as const;

export type CaseStudyHeroVideoVariant = 'cinematic' | 'ambient';

interface CaseStudyHeroVideoProps {
  /** Path under /public to the .mp4 */
  src: string;
  /** Seconds to seek to for the static poster frame */
  startAt?: number;
  /** Aspect ratio of the card (default "21/9") */
  aspectRatio?: string;
  /**
   * - `cinematic` (default): click-to-play with sound, hover controls.
   * - `ambient`: silent autoplay loop, no controls, non-interactive.
   */
  variant?: CaseStudyHeroVideoVariant;
}

/**
 * Case study hero video.
 *
 * Cinematic variant:
 * - Renders a poster frame at `startAt` while paused.
 * - Big centered "Play with sound" overlay until the first user gesture.
 * - After start: small bottom-right hover controls (Play/Pause + Mute).
 *
 * Ambient variant:
 * - Silent autoplay, looping, no overlays, no controls.
 */
export const CaseStudyHeroVideo = ({
  src,
  startAt,
  aspectRatio = '21/9',
  variant = 'cinematic',
}: CaseStudyHeroVideoProps) => {
  const { locale } = useTranslation();
  const v_ = locale === 'ar' ? VIDEO_LABELS.ar : VIDEO_LABELS.en;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || startAt === undefined) return;
    const seek = () => {
      try {
        v.currentTime = startAt;
      } catch {
        // ignore
      }
    };
    if (v.readyState >= 1) seek();
    else v.addEventListener('loadedmetadata', seek, { once: true });
    return () => v.removeEventListener('loadedmetadata', seek);
  }, [startAt]);

  const startWithSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setMuted(false);
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        v.muted = true;
        setMuted(true);
        v.play().catch(() => {});
      });
    }
    setHasStarted(true);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      v.pause();
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    if (!next && v.paused) {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
    setMuted(next);
  };

  if (variant === 'ambient') {
    return (
      <div
        className="radius-18 relative w-full overflow-hidden rounded-[2.5rem] bg-neutral-100 ring-1 ring-neutral-900/5 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.25)] [clip-path:inset(0_round_18px)] [contain:paint] [transform:translateZ(0)] md:rounded-[3rem]"
        style={{ aspectRatio }}
      >
        <video
          ref={videoRef}
          src={src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
          disablePictureInPicture
          controlsList="nodownload noplaybackrate nofullscreen"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover select-none"
        />
      </div>
    );
  }

  return (
    <div
      className="radius-18 group relative w-full overflow-hidden rounded-[2.5rem] bg-neutral-100 ring-1 ring-neutral-900/5 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.25)] [clip-path:inset(0_round_18px)] [contain:paint] [transform:translateZ(0)] md:rounded-[3rem]"
      style={{ aspectRatio }}
    >
      <video
        ref={videoRef}
        src={startAt !== undefined ? `${src}#t=${startAt}` : src}
        muted
        loop
        playsInline
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
        className="absolute inset-0 h-full w-full object-cover"
      />

      {!hasStarted && (
        <button
          type="button"
          onClick={startWithSound}
          aria-label={v_.playWithSound}
          className={cn(
            // radius-18 to match the video it covers. Without it the global
            // button-pill rule clamped this 1216x684 hit area to a 342px
            // stadium, painting bg-black/15 as a grey lozenge over the frame.
            'group/play radius-18 absolute inset-0 z-20 flex flex-col items-center justify-center',
            'bg-black/15 transition-colors duration-200 hover:bg-black/25',
            'focus:outline-none',
          )}
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-neutral-900 shadow-[0_8px_30px_-8px_rgba(0,0,0,0.4)] backdrop-blur-md transition-transform duration-200 group-hover/play:scale-105 sm:h-20 sm:w-20">
            <Play className="ml-1 h-6 w-6 sm:h-7 sm:w-7" fill="currentColor" />
          </span>
          <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-md sm:text-sm">
            <Volume2 className="h-3.5 w-3.5" />
            {v_.playWithSoundShort}
          </span>
        </button>
      )}

      {hasStarted && (
        <div
          className={cn(
            'absolute bottom-4 z-10 ltr:right-4 rtl:left-4 flex items-center gap-2',
            'transition-opacity duration-200',
            'opacity-0 group-hover:opacity-100 focus-within:opacity-100',
          )}
        >
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? v_.pause : v_.play}
            aria-pressed={!playing}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full',
              'bg-white/85 text-neutral-900 backdrop-blur-md transition-all',
              'hover:scale-105 hover:bg-white',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
            )}
          >
            {playing ? (
              <Pause className="h-4 w-4" fill="currentColor" />
            ) : (
              <Play className="ml-0.5 h-4 w-4" fill="currentColor" />
            )}
          </button>
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? v_.unmute : v_.mute}
            aria-pressed={!muted}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full',
              'bg-white/85 text-neutral-900 backdrop-blur-md transition-all',
              'hover:scale-105 hover:bg-white',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
            )}
          >
            {muted ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default CaseStudyHeroVideo;
